<template>
    <RightDrawer :open="open" title="Create Notification" @close="$emit('cancel')">    
        <template #default>
            <!-- Description -->
            <div class="mb-6">
                <p class="text-sm text-bhav-cool-gray-600">
                    Select a community below to create a notification. Once it has been created, you can choose which items you want to be notified about.
                </p>
            </div>

            <!-- Community Selection -->
            <div class="flex flex-col gap-2 flex-1">
                <label for="community_select" class="block text-sm font-medium leading-5 text-bhav-cool-gray-900">
                    Communities <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                </label>
                <div class="flex-1">
                    <SelectField
                        id="community_select"
                        v-model="selectedCommunity"
                        :allow-null="false"
                        empty="Please Select a Community..."
                        :options="communityOptions"
                        :disabled="creating"
                        :float="false"
                    />
                </div>
                <div v-if="creating" class="text-sm text-bhav-cool-gray-600 text-center mt-2">
                    Creating notification...
                </div>
            </div>
        </template>
    </RightDrawer>
</template>

<script>
import { SelectField } from '@/components/form';
import { useMessageStore } from '@/stores/message';
import { CommunitiesController } from '@/controllers/communities';
import { NotificationsController } from '@/controllers/notifications';
import Notification from '@/models/notification';
import RightDrawer from './RightDrawer.vue';

export default {
    name: 'NewNotification',
    setup() {
        const messageStore = useMessageStore();
        return {
            messageStore
        }
    },
    emits: [
        'notification-created',
        'cancel'
    ],
    props: {
        userId: {
            type: [Number, null],
            required: true
        },
        notifications: {
            type: Array,
            required: true,
            default: () => []
        },
        open: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedCommunity: null,
            communities: [],
            creating: false
        }
    },
    watch: {
        selectedCommunity: {
            handler(next) {
                if (next) {
                    this.createNotification();
                }
            }
        },
        open: {
            handler(next) {
                if(next) {
                    // ensure there are no selections each time it opens
                    this.selectedCommunity = null;
                }
            }
        }
    },
    computed: {
        communityOptions() {
            // Get list of community IDs that have active notifications
            const activeCommunityIds = this.notifications
                .filter(notification => notification.notifiable_id && notification.active)
                .map(notification => notification.notifiable_id);

            // Include communities that don't have active notifications (either no notification or inactive)
            const availableCommunities = this.communities.filter(community =>
                !activeCommunityIds.includes(community.id)
            );

            return availableCommunities.map(community => ({
                value: community.id,
                label: community.name || 'Unknown Community'
            }));
        }
    },
    methods: {
        async loadCommunities() {
            await CommunitiesController.index()
            .then(resp => {
                this.communities = resp.filter(community => community.status != 'inactive');
            })
            .catch(error => {
                console.error('Error loading communities:', error);
                this.messageStore.setAlert({
                    text: 'Failed to load communities',
                    level: 'error'
                });
            });
        },
        async createNotification() {
            if (!this.selectedCommunity) return;

            this.creating = true;
            try {
                // Check if notification already exists for this community
                const existingNotification = this.notifications.find(
                    notification => notification.notifiable_id === this.selectedCommunity
                );


                let response;
                if (existingNotification && !existingNotification.active) {
                    // Restore inactive notification
                    response = await NotificationsController.restore(existingNotification);
                } else {
                    // Create new notification
                    const newNotification = new Notification({
                        user_id: this.userId,
                        notifiable_type: 'division',
                        notifiable_id: this.selectedCommunity,
                        active: true
                    });
                    response = await NotificationsController.store(newNotification);
                }

                this.$emit('notification-created', response);
                this.$emit('cancel'); 
            } catch (error) {
                this.messageStore.setAlert({
                    text: 'Failed to create notification',
                    level: 'error'
                });
            } finally {
                this.creating = false;
            }
        }
    },
    mounted() {
        this.loadCommunities();
    },
    components: {
        SelectField,
        RightDrawer
    }
}
</script>

